import { cloneDeep } from 'lodash';

import type { HotelIngredient } from '@apps/main/modules/recipe/ingredients';

import { getHotelConstraintQL } from '@libs/search-service-smart-search';
import type {
  HotelConstraintQL,
  HotelIngredientAdapter,
} from '@libs/trip-planner-interfaces';
import {
  AccommodationRule,
  IngredientType,
  AmenitiesFilter,
  ExperienceFilter,
} from '@libs/trip-planner-interfaces';
import {
  FullTextConstraint,
  PolygonIdConstraint,
} from '@libs/constraint-builder';

import { getBaseConstraint } from '../baseIngredientAdapter';

import { adaptActivityIngredientToItinerarySearchCriteria } from './activityIngredientItinerarySearchAdapter';

export function adaptHotelIngredientToItinerarySearchCriteria(
  ingredient: HotelIngredient
): HotelIngredientAdapter {
  const constraint = getHotelIngredientAdapterConstraint(ingredient);
  const hotel: HotelIngredientAdapter = {
    ingredientType: IngredientType.Hotel,
    ingredientId: ingredient.ingredientId,
    numberOfNights: ingredient.numberOfNights,
    constraint,
    priceFilters: ingredient.filters.priceFilters,
    heartedHotels: ingredient.heartedHotels,
    subIngredients: ingredient.subIngredients.map(
      adaptActivityIngredientToItinerarySearchCriteria
    ),
    suggestedBy: ingredient.availabilityOrder,
  };

  return cloneDeep(hotel);
}

export function getHotelIngredientAdapterConstraint(
  ingredient: HotelIngredient
): HotelConstraintQL {
  const constraint = getBaseConstraint(
    ingredient.searchTerm,
    ingredient.filters.polygonIds
  );
  if (ingredient.polygonConstraint) {
    constraint.selection = ingredient.polygonConstraint;
  } else if (ingredient.nearbyPolygon) {
    const { id, name } = ingredient.nearbyPolygon;

    constraint.selection = id
      ? new PolygonIdConstraint(id)
      : name
        ? new FullTextConstraint(name)
        : constraint.selection;
  }

  constraint.starRating = ingredient.filters.starRating;

  constraint.onlyHotelsWithPool =
    ingredient.filters.selectedAmenitiesFilters.includes(AmenitiesFilter.Pool);
  constraint.onlyHotelsWithParking =
    ingredient.filters.selectedAmenitiesFilters.includes(
      AmenitiesFilter.Parking
    );
  constraint.onlyHotelsWithSpa =
    ingredient.filters.selectedAmenitiesFilters.includes(AmenitiesFilter.Spa);
  constraint.onlyHotelsWithGym =
    ingredient.filters.selectedAmenitiesFilters.includes(AmenitiesFilter.Gym);
  constraint.onlyHotelsWithWifi =
    ingredient.filters.selectedAmenitiesFilters.includes(AmenitiesFilter.Wifi);
  constraint.onlyHotelsWithAir =
    ingredient.filters.selectedAmenitiesFilters.includes(
      AmenitiesFilter.AirConditioned
    );
  constraint.onlyHotelsWithVehicleChargingStation =
    ingredient.filters.selectedAmenitiesFilters.includes(
      AmenitiesFilter.VehicleChargingStation
    );
  constraint.onlyHotelsWithPetsAllowed =
    ingredient.filters.selectedExperienceFilters.includes(
      ExperienceFilter.PetsAllowed
    );
  constraint.onlyHotelsWithBusinessProperty =
    ingredient.filters.selectedExperienceFilters.includes(
      ExperienceFilter.Business
    );
  constraint.onlyHotelsWithFamilyProperty =
    ingredient.filters.selectedExperienceFilters.includes(
      ExperienceFilter.Family
    );

  constraint.onlyHotelsWithGreenSustainableProperty =
    ingredient.filters.selectedExperienceFilters.includes(
      ExperienceFilter.GreenSustainableProperty
    );

  constraint.onlyHotelsWithFreeCancellation =
    ingredient.filters.selectedHotelRules.includes(
      AccommodationRule.FreeCancellation
    );

  constraint.maxNumberOfHotels = 200;

  if (ingredient.filters.filterText) {
    constraint.hotelName = ingredient.filters.filterText;
  }

  const hotelConstraint = getHotelConstraintQL({
    ...constraint,
    selectedHotelTypes: ingredient.getSelectedAccommodationTypes(),
    selectedBoardBasis: ingredient.filters.boardBasis,
    polygonIds: ingredient.filters.polygonIds,
  });

  hotelConstraint.freeCancellation =
    ingredient.filters.selectedHotelRules.includes(
      AccommodationRule.FreeCancellation
    );

  return hotelConstraint;
}
